import { Component, Host, h, Element, Prop, Listen, Event, EventEmitter, State, Method } from '@stencil/core';
import { DataService } from '../../utils/data-service.service';

@Component({
  tag: 'bam-form',
  styleUrl: 'bam-form.scss',
})
export class BamForm {
  @Element() el: HTMLElement;
  @Prop() htmlId: string;
  @Prop() action: string;
  @Prop() enctype: string;
  @Prop() ajaxUrl: string;
  @Prop() delaySubmit?: number;
  @Prop() formClass = 'grid is-multiline mt-none';
  @Prop() submitOnChange? = false;
  @Prop() showFormAfterSuccess? = false;
  @Prop() method = 'POST';
  @Prop() noSubmit = false;
  @Prop() isSaveForm = false;
  @Prop() headline?: string;
  @Prop() description?: string;
  @Prop() saveBtnText?: string;
  @Prop() prefillReplaceKeys?: string = '';
  @State() isDelaying = true;
  @State() statusMessage = this.description;
  @State() statusButtonText: string;
  @State() submitterName: string;
  @State() newHeadline = this.headline;
  @State() loading = false;
  @State() statusColor: BalProps.BalTextColor = '';
  @State() statusSize: BalProps.BalTextSize = '';
  @State() showForm = true;
  private parentForm: HTMLFormElement;
  private form: HTMLFormElement;
  private inputs: NodeListOf<HTMLBamFieldElement | HTMLBamFileUploadElement>;
  private submitAfterRender = false;
  private parentModal?: HTMLBalModalElement;
  private modal?: HTMLBalModalElement;
  private prefillOptions: object;
  @Event() formInvalid: EventEmitter;
  @Event() formSuccess: EventEmitter;

  @Listen('balChange')
  onBalChange() {
    if (this.submitOnChange) {
      setTimeout(() => this.handleSubmitEvent(), 100);
    }
  }

  @Listen('submit')
  onSubmit(e) {
    e.preventDefault();
    this.submitterName = e.submitter.name;
    if (this.submitterName === 'mgnlFormBackButtonPressed') {
      let formData = new FormData(this.form);
      formData.append(e.submitter.name, 'true');
      this.submitAfterRender = true;
    } else {
      // Timeout set to not be triggered before actual input field validation is finised in "bam-field.tsx"
      setTimeout(() => this.handleSubmitEvent(), 50);
    }
  }

  handleSubmitEvent() {
    this.inputs.forEach(function (input) {
      if (input.offsetParent !== null) {
        input.validateInput();
        input.switchInputDisabled(true);
      }
    });
    let formValid = this.form.checkValidity();

    if (formValid) {
      if (window.grecaptcha && !this.isSaveForm) {
        const captchaResponse = document.getElementById('g-recaptcha-response') as HTMLFormElement;
        window.grecaptcha
          .execute()
          .then(token => {
            captchaResponse.value = token;
          })
          .catch(() => {
            this.statusColor = 'danger';
            this.statusSize = 'small';
            this.statusMessage = 'Captcha failed!';
          });
      } else {
        if (this.delaySubmit) {
          this.openModal();
          setTimeout(this.submitOrAjax.bind(this), this.delaySubmit);
        } else {
          this.submitOrAjax();
        }
      }
    } else {
      this.focusFirstInvalid();
    }

    setTimeout(() => {
      this.focusFirstInvalid();
    }, 50);
  }

  submitOrAjax() {
    if (this.ajaxUrl) {
      this.doAjax();
    } else {
      !this.noSubmit && this.form.submit();
    }

    this.inputs.forEach(function (input) {
      if (input.offsetParent !== null) {
        input.switchInputDisabled(false);
      }
    });
  }

  doAjax() {
    this.loading = true;
    let data = new FormData(this.form);
    const parentFormData = this.isSaveForm ? new FormData(this.parentForm) : undefined;
    let queryString = new URLSearchParams(new FormData(this.form) as any);
    let ajaxPromise;

    if (this.method === 'POST') {
      if (parentFormData) {
        const parentEntries = Object.fromEntries(parentFormData.entries());

        for (const entry in parentEntries) {
          data.append(entry, parentEntries[entry]);
        }
      }
      ajaxPromise = DataService.postData(this.ajaxUrl, data);
    } else {
      ajaxPromise = DataService.getData(this.ajaxUrl + '?' + queryString);
    }

    ajaxPromise.then(data => {
      this.statusMessage = data.message ? data.message : data.error ? data.error?.text : data.success?.text;
      this.showForm = false;

      if (data.error) {
        this.statusColor = 'danger';
        this.statusSize = 'small';
        this.statusButtonText = data.error.buttonText ?? '';
        this.loading = false;
        return;
      }

      this.formSuccess.emit(data);
      this.statusColor = 'success';
      this.statusSize = 'block';
      this.newHeadline = data.success?.title ?? '';
      this.statusButtonText = data.success?.buttonText ?? '';
      this.loading = false;
      this.showForm = this.showFormAfterSuccess;
    });
  }

  componentDidLoad() {
    this.form = this.el.querySelector('form');
    this.inputs = this.el.querySelectorAll('bam-field, bam-file-upload');
    if (this.isSaveForm) {
      this.parentForm = this.el.closest('form');
      this.parentModal = this.el.closest('bal-modal');
    }
    if (this.prefillReplaceKeys !== '') {
      const options = JSON.parse(this.prefillReplaceKeys);
      this.prefillOptions = options;
      if (options.prefillUrl) {
        fetch(options.prefillUrl).then(response => {
          response.json().then(data => {
            this.loopOverKeys(data);
          });
        });
      }
    }

    //Validate inputs if checkbox or radio values are prefilled on load
    this.inputs.forEach(input => {
      if (input.offsetParent !== null && input.querySelector('[aria-checked="true"]')) {
        input.validateInput();
      }
    });
  }

  loopOverKeys(data) {
    Object.keys(data).forEach(key => {
      if (data[key] !== null && typeof data[key] === 'object') {
        this.loopOverKeys(data[key]);
      } else {
        const remapToFields = this.prefillOptions['remapToFields'];
        let remapField = key;
        if (remapToFields) {
          remapField = remapToFields[key];
        }
        if (remapField !== undefined) {
          const input: HTMLFormElement = this.form.querySelector(`[name="${remapField}"]`);
          if (input) {
            if (input.tagName === 'BAL-RADIO') {
              const radioGroup = input.closest('bal-radio-group');
              radioGroup.value = `${data[key]}`;
            } else {
              input.value = data[key];
              input.checked = true;
            }
          }
        }
      }
    });
  }

  componentDidRender() {
    if (this.submitAfterRender) {
      this.submitOrAjax();
      this.submitAfterRender = false;
    }
  }

  @Method()
  async resetOverlayState() {
    const emailInput: HTMLBalInputElement = this.form.querySelector('bal-input[name="saveFormMail"]');
    const checkbox: HTMLBalCheckboxElement = this.form.querySelector('bal-checkbox[name="saveFormDataPageCheckbox"]');

    this.showForm = true;
    this.statusMessage = this.description;
    this.statusButtonText = '';
    this.statusColor = '';
    this.statusSize = '';
    this.newHeadline = this.headline;
    emailInput.value = '';
    checkbox.checked = false;
  }

  handleClick = async () => {
    await this.parentModal.close();
  };

  focusFirstInvalid() {
    let firstInvalidInput = this.form
      .querySelector('input:invalid, select:invalid, textarea:invalid')
      ?.closest('bal-input, bal-textarea, bal-checkbox, bal-radio, bal-dropdown, bal-date, bal-file-upload') as HTMLBalInputElement;
    if (firstInvalidInput) {
      firstInvalidInput?.setFocus();
    }
  }

  openModal = async () => {
    await this.modal.open();
  };

  render() {
    const { htmlId, action, enctype, formClass, method, saveBtnText } = this;

    return (
      <Host>
        <bal-modal class="bam-form--modal" ref={(el: HTMLBalModalElement) => (this.modal = el)} component={this.modal}>
          <slot name="delayModal" />
        </bal-modal>

        {this.headline && (
          <bal-heading class="mt-normal" level="h4">
            {this.newHeadline}
          </bal-heading>
        )}
        <bal-text class={{ 'hidden': !this.statusMessage }} color={this.statusColor} size={this.statusSize}>
          {this.statusMessage}
        </bal-text>
        <form id={htmlId} class={{ 'formClass': !!formClass, 'hidden': !this.showForm }} method={method} action={action} enctype={enctype} novalidate>
          <slot></slot>
          {this.submitterName && <input type="hidden" name={this.submitterName} value="true" />}
          <fieldset class={{ 'hidden': !this.isSaveForm }} aria-label="submit">
            <bam-submit>
              <bal-button loading={this.loading} elementType="submit">
                {saveBtnText}
              </bal-button>
            </bam-submit>
          </fieldset>
        </form>

        <bal-button class={{ 'mt-large': true, 'hidden': !this.statusButtonText }} color="info" onClick={this.handleClick}>
          {this.statusButtonText}
        </bal-button>
      </Host>
    );
  }
}
